import React from "react";
import { MapPin, Tractor, Hammer, House, Wind, ShieldCheck, Siren } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
const serviceAreas = ["Workflow Optimization", "Real-time Reporting", "Document Intelligence", "Strategic Consulting"];
const specializedServices = [{
  title: "AI Workflow Automation",
  description: "Eliminate repetitive tasks with intelligent triggers and actions that connect your entire enterprise stack seamlessly.",
  icon: Tractor
}, {
  title: "Business Dashboards",
  description: "Transform raw data into high-fidelity visual command centers with real-time reporting and predictive analytics.",
  icon: Hammer
}, {
  title: "AI Document Assistants",
  description: "Automate data extraction and classification from complex documents using advanced natural language processing.",
  icon: House
}, {
  title: "Data Analysis & Insights",
  description: "Uncover hidden patterns and growth opportunities with deep-learning models tailored to your specific industry.",
  icon: Wind
}, {
  title: "Automation Consulting",
  description: "Expert guidance on scaling your AI infrastructure while maintaining enterprise-grade security and compliance.",
  icon: ShieldCheck
}, {
  title: "Rapid Implementation",
  description: "Deploy custom automation solutions in weeks, not months, with our agile development and integration framework.",
  icon: Siren
}];
export default function OurAutomationSuite() {
  return <section data-section-id="256962" id="automation" className="w-full flex flex-col bg-background text-foreground">
      {}
      <div className="bg-background py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center">
            
            {}
            <div className="relative rounded-xl overflow-hidden shadow-2xl border border-white/10">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1688847/img/datazen_dashboard_preview.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1688847/img/datazen_dashboard_preview.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1688847/img/datazen_dashboard_preview.png-wvc-sizes" alt="DataZen enterprise dashboard interface" className="w-full h-auto object-cover aspect-[4/3] opacity-90" loading="lazy" />
            </div>

            {}
            <div className="flex flex-col gap-6">
              <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight tracking-tight">
                The Intelligence Layer Your Business Deserves
              </h2>
              
              <div className="space-y-4 text-muted-foreground text-lg leading-relaxed font-default">
                <p>
                  DataZen is the mission control for modern enterprises. We bridge the gap between complex data silos and actionable intelligence, providing a unified suite of AI tools designed for scale and precision.
                </p>
                <p>Our platform leverages "Glassmorphism 2.0" design principles to ensure your data isn't just accessible, it's intuitive. We bring enterprise-level expertise with a focus on minimal, high-performance automation that saves thousands of manual hours.</p>
              </div>

              {}
              <div className="flex flex-wrap gap-3 pt-2">
                {serviceAreas.map((area, i) => <div key={i} data-index={i} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary/10 text-primary text-sm font-medium border border-primary/20">
                    <MapPin className="size-4" />
                    {area}
                  </div>)}
              </div>
            </div>

          </div>
        </div>
      </div>

      {}
      <div className="bg-card/30 py-16 md:py-24 border-t border-border/50 relative overflow-hidden">
        {}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]"></div>
        
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          
          {}
          <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16 flex flex-col items-center gap-4">
            <span className="text-primary font-semibold tracking-widest text-sm uppercase">
              Our Automation Suite
            </span>
            <h2 className="font-default text-3xl md:text-4xl font-bold text-foreground tracking-tight">
              Specialized AI Solutions
            </h2>
            <p className="text-muted-foreground text-lg font-default">
              Every enterprise workflow is unique. Here is how DataZen optimizes yours.
            </p>
          </div>

          {}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {specializedServices.map((service, i) => {
            const Icon = service.icon;
            return <Card key={i} data-index={i} className="border border-white/10 shadow-md hover:shadow-primary/10 hover:border-primary/30 transition-all duration-300 bg-card/50 backdrop-blur-md group">
                  <CardHeader className="pb-4">
                    <div className="size-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                      <Icon className="size-6" />
                    </div>
                    <CardTitle className="font-default text-xl text-foreground">
                      {service.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base leading-relaxed text-muted-foreground font-default">
                      {service.description}
                    </CardDescription>
                  </CardContent>
                </Card>;
          })}
          </div>

        </div>
      </div>
    </section>;
}